import { truncate } from '@shared/utils'

// Builds a compact, investor-friendly address from a property's address
// fields (the parser maps the API's snake_case fields onto AddressParts).
// Raw API rows look like:
//
//   {
//     address_line_1: "Unit 3205, Tower 1",
//     address_line_2: "The Address Residence Fountain Views",
//     city: "Downtown Dubai",
//     state_province: "Dubai"
//   }
//
// On a marketplace card the title already tells you which property it is, so
// the unit/floor (address_line_1) is noise — we lead with the community/building
// (address_line_2), then district, then emirate. Adjacent duplicates like
// "Dubai, Dubai" are collapsed; if the joined string still exceeds `maxChars`
// it gets a trailing ellipsis so the line never stretches under the title.

export interface AddressParts {
  addressLine2?: string | null
  city?: string | null
  stateProvince?: string | null
}

const DEFAULT_MAX_CHARS = 56
const FALLBACK = 'Location TBC'

export function formatPropertyAddress(
  parts: AddressParts,
  maxChars: number = DEFAULT_MAX_CHARS,
): string {
  const segments = [parts.addressLine2, parts.city, parts.stateProvince]
    .map((segment) => segment?.trim())
    .filter((segment): segment is string => Boolean(segment))

  // Collapse adjacent duplicates case-insensitively — common in UAE data where
  // city and emirate both read "Dubai".
  const deduped: string[] = []
  for (const segment of segments) {
    if (deduped.at(-1)?.toLowerCase() !== segment.toLowerCase()) {
      deduped.push(segment)
    }
  }

  if (deduped.length === 0) return FALLBACK
  return truncate(deduped.join(', '), maxChars)
}
